import Cookies from "js-cookie";
import ms, { StringValue } from "ms";
import urlcat from "urlcat";

import logger from "@/runtime-utils/observability/logger";
import { getPublicNodeEnv } from "@/runtime-utils/config/public-env";
import { deletePushSubscription } from "@/services/pwa";
import { clearSupportMode } from "@/utils/support-access";
import { clearSessionDeadline } from "@/session/store";
import { ADMIN_SITE_URL, API_URL, axiosInstance, parseResponse } from "..";
import { ILogin, IRegister } from "../../interfaces/IAuth";

const AUTH_URL = `${API_URL}/auth`;
const TERM_URL = `${API_URL}/tenant/auth`;
const PUBLIC_NODE_ENV = getPublicNodeEnv();

const removeAuthCookies = () => {
  const removalVariants: Array<Parameters<typeof Cookies.remove>[1]> = [
    { path: "/" },
    // Also try env/domain-specific variants in case older sessions were set with these.
    ...(PUBLIC_NODE_ENV === "production"
      ? [
          { path: "/", secure: true },
          { path: "/", domain: ".wisterias.co.uk", secure: true },
        ]
      : []),
    ...(PUBLIC_NODE_ENV === "test"
      ? [
          { path: "/", secure: true },
          { path: "/", domain: ".wisterias.co.uk", secure: true },
          { path: "/", domain: ".test.wisterias.co.uk", secure: true },
        ]
      : []),
  ];

  for (const options of removalVariants) {
    Cookies.remove("token", options);
    Cookies.remove("tenant", options);
  }

  // Final fallback (default cookie scope)
  Cookies.remove("token");
  Cookies.remove("tenant");

  if (typeof window !== "undefined") {
    window.localStorage.removeItem("tenant");
  }
};

const calculateExpiryInDays = (rawExpiry: any): number | undefined => {
  const hasValidExpiry =
    typeof rawExpiry === "string" && rawExpiry.trim().length > 0;
  return hasValidExpiry
    ? ms(rawExpiry as StringValue) / (1000 * 60 * 60 * 24)
    : undefined;
};

const getAuthCookieOptions = (expiryInDays?: number) => {
  const baseOptions =
    typeof expiryInDays === "number" ? { expires: expiryInDays } : {};

  if (PUBLIC_NODE_ENV === "production" || PUBLIC_NODE_ENV === "test") {
    return {
      ...baseOptions,
      path: "/",
      sameSite: "lax" as const,
      secure: true,
    };
  }

  return {
    path: "/",
    sameSite: "lax" as const,
  };
};

const persistAuthData = (tenantId: string, expiryInDays?: number) => {
  const cookieOptions = getAuthCookieOptions(expiryInDays);
  Cookies.set("tenant", tenantId, cookieOptions);

  if (typeof window !== "undefined") {
    window.localStorage.setItem("tenant", tenantId);
  }
};
export const login = async (data: ILogin) => {
  try {
    const response = await axiosInstance.post(`${AUTH_URL}/login`, data);
    const parsedResponse = parseResponse(response);

    if (parsedResponse.success && parsedResponse?.data?.approved) {
      const expiryInDays = calculateExpiryInDays(parsedResponse?.expiresIn);
      const tenantId =
        parsedResponse?.data?.tenantId ||
        parsedResponse?.data?.tenant?._id ||
        parsedResponse?.data?.tenant ||
        parsedResponse?.tenantId;

      if (tenantId) {
        persistAuthData(tenantId, expiryInDays);
      }
    }
    return parsedResponse;
  } catch (error: any) {
    // 401 here can be expected when the same form is used to try support-mode
    // admin credentials via fallback (/admin/login). Keep console noise low.
    if (error?.response?.status !== 401) {
      logger.error({ error }, "login error");
    }
    if (error?.response) {
      return parseResponse(error.response);
    }

    return {
      success: false,
      message: error?.message || "Network error while signing in",
    };
  }
};

const removeCurrentPushSubscription = async () => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  await deletePushSubscription(subscription.endpoint);
  await subscription.unsubscribe();
};

export const logOutUser = async (disconnectSocket?: () => void) => {
  try {
    await removeCurrentPushSubscription();
  } catch (error) {
    logger.warn({ error }, "Push subscription cleanup failed during logout");
  } finally {
    removeAuthCookies();
    clearSessionDeadline();
    clearSupportMode();
    if (disconnectSocket) disconnectSocket();
  }
};

export const register = async (data: IRegister) => {
  try {
    const response = await axiosInstance.post(`${AUTH_URL}/register`, data);
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const checkPassword = async (data: any) => {
  try {
    const response = await axiosInstance.post(
      `${AUTH_URL}/check-password`,
      data
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const sendResetMail = async (data: any) => {
  try {
    const response = await axiosInstance.post(
      `${AUTH_URL}/send-reset-mail`,
      data
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const checkResetPasswordLink = async (
  userId: string,
  token: string,
  type: "pin" | "password"
) => {
  try {
    const url = urlcat(`${AUTH_URL}/check-reset-link/:userId`, {
      userId,
      token,
      type,
    });
    const response = await axiosInstance.get(url);
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const resetPassword = async (data: any) => {
  try {
    const response = await axiosInstance.post(
      `${AUTH_URL}/reset-password`,
      data
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};
export const resetPIN = async (data: any) => {
  try {
    const response = await axiosInstance.post(`${AUTH_URL}/reset-pin`, data);
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const updateProfile = async (id: string, data: any) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.put(
      `${AUTH_URL}/profile/${id}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const changePassword = async (id: string, data: any) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.put(
      `${AUTH_URL}/change-password/${id}`,
      data
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const updatePIN = async (data: any) => {
  try {
    const response = await axiosInstance.put(`${AUTH_URL}/update-pin`, data);
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const updatePreferredTerms = async (id: string, data: any) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.post(
      `${TERM_URL}/preferred-term/${id}`,
      data
    );
    const parsedResponse = parseResponse(response);
    return parsedResponse;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const gePreferred = async (id: string | undefined) => {
  try {
    if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
      throw new Error("Invalid ID");
    }
    const response = await axiosInstance.get(
      `${TERM_URL}/preferred-term/${id}`
    );
    return parseResponse(response);
  } catch (error: any) {
    // Tenants created before preferred terms were introduced legitimately have
    // no record. Treat that case as an empty response rather than surfacing a
    // noisy network error during application bootstrap.
    if (error?.response?.status === 404) {
      return { success: true, data: null };
    }
    return parseResponse(error.response);
  }
};

export const geTermsOptions = async () => {
  try {
    const response = await axiosInstance.get(`${ADMIN_SITE_URL}preferred`);
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};
